import { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { selectMonitorSessions } from 'src/store/slices/call-slice';
import { selectSelectedSpeaker } from 'src/store/slices/audio-device-slice';

export default function CallAudioManager({ sessions }) {
  const monitorSessions = useSelector(selectMonitorSessions);

  return (
    <>
      {[...sessions, ...monitorSessions].map((session) => (
        <CallAudio
          key={session.id.startsWith('monitor-') ? session.id : `session-${session.id}`}
          session={session}
        />
      ))}
    </>
  );
}

function CallAudio({ session }) {
  const audioRef = useRef(null);
  const selectedSpeaker = useSelector(selectSelectedSpeaker);

  // Apply selected speaker device when it changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !selectedSpeaker) return undefined;

    // Check if browser supports setSinkId
    if (!audio.setSinkId) {
      console.warn('Browser does not support audio output device selection');
      return undefined;
    }

    // Store current playback state
    const wasPlaying = !audio.paused;
    const { currentTime } = audio;

    audio
      .setSinkId(selectedSpeaker)
      .then(() => {
        // Resume playback if it was playing
        if (wasPlaying && audio.paused) {
          audio.currentTime = currentTime;
          return audio
            .play()
            .catch((err) => console.warn('Could not resume playback after speaker change:', err));
        }
        return undefined;
      })
      .catch((err) => {
        console.error('Error changing speaker:', err);
      });

    return undefined;
  }, [selectedSpeaker, session.id]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (session.remoteStream) {
      audio.pause();
      audio.srcObject = session.remoteStream;

      audio
        .play()
        .then(() => console.log(`🔊 Playing remote stream for session: ${session.id}`))
        .catch((err) => console.warn(`🚫 Remote stream play error: ${session.id}`, err));
    } else if (session.status === 'ringing') {
      audio.src = '/media/ringtone.mp3';
      audio.loop = true;

      audio
        .play()
        .then(() => console.log(`🎵 Playing ringtone for session: ${session.id}`))
        .catch((err) =>
          console.warn(`🚫 Ringtone autoplay blocked for session: ${session.id}`, err)
        );
    } else {
      audio.pause();
      audio.srcObject = null;
      audio.src = '';
    }

    // eslint-disable-next-line consistent-return
    return () => {
      audio.pause();
      audio.srcObject = null;
      audio.src = '';
    };
  }, [session.id, session.remoteStream, session.status]);

  return (
    <audio id={`call-audio-${session.id}`} ref={audioRef} autoPlay hidden>
      <track kind="captions" />
    </audio>
  );
}
